import "./Common.css";
import "./DoorLock.css";

function DoorLock() {
	const circuitDiagramUrl =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQSO3ste4XqUT79ZJkkrtIQXAVKpRk9goRuLwNm94PJhR_E?width=1919&height=1079";
	const testingUrl =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQSXX4fZoR84TIBQMw1oCZMJAf9nFsT6vJjosFYWQF49ekQ";
	const dispcir =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQQKfB59LY0RRJtT9vWwAzWuAcAsTsbtfbQPXY9gzEri83s?width=821&height=770";
	const disppcb =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQR9BIobxbS2QpTI1QO7CHjwAQqJT64E5BoDODTr2xORaWI";
	const keycir =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQQqGos9Je9sRKYqLb7hrm78AeWSj45dluQsOX0iereSx5M";
	const keypcb =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQQLGVGMC6jdR6Qo7N2vHdvRAf0vVG3L-L-7JMvQYpp6H9s";
	const finalpcb =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQQKdgUwdtDKTJylrgmiX_DxAcvaQVoa-N4pY5wz-qu-64g?width=907&height=1280";
	const finalphoto =
		"https://1drv.ms/i/c/c94efc88b6ce9f63/IQQrUVieyOt4TYfsUelH026MAYkFehS2invqPMxpqmg49Bc?width=502&height=1600";

	const openImageInNewWindow = (url) => {
		window.open(url, "_blank", "noopener,noreferrer");
	};

	const renderFigure = (url, alt, caption) => (
		<figure className="media-figure">
			<div className="media-figure__visual">
				<img src={url} alt={alt} loading="lazy" />
				<button
					type="button"
					className="view-full-button"
					onClick={() => openImageInNewWindow(url)}
				>
					View full image
				</button>
			</div>
			{caption ? (
				<figcaption className="figcaption-text">{caption}</figcaption>
			) : null}
		</figure>
	);

	return (
		<main className="content">
			<section id="project-door-lock" className="section doorlock">


				<h1 className="text-head"> Door Lock System </h1>

				<div className="project-container">
					<p className="project-text">
						This project showcases a custom-designed{" "}
						<strong>Digital Electronic Door Locking System</strong>, developed
						as a practical application of the foundational principles of
						electronics. By integrating theoretical knowledge from First-Year
						Analog Electronics, Electronics, and hands-on Digital Electronic
						Lab modules, I engineered a hardware-based security solution that
						prioritizes logic and reliability.
					</p>
				</div>
				<br />
				<br />
				<h1 className="text-head"> System Design and Functionality</h1>

				<div className="project-intro-with-image">
					<p className="project-text">
						The system features a numerical keypad allowing users to input a
						specific security code. To manage data flow and verify the sequence,
						I utilized counters and SR latches. These components work together
						to cross-check the entered digits against the preset code. A
						7-segment display provides real-time visual feedback, showing the
						numbers as they are entered to ensure user accuracy.

						<br />

						Security is managed through a multi-step validation process. Once a
						three-digit sequence is entered, the user presses an "OK" button to
						attempt the unlock. If the sequence is correct, the system triggers
						the locking mechanism. If incorrect, the system automatically resets
						upon pressing the "OK" button, prompting the user to re-enter the
						code.

						<br />

						To prevent brute-force attacks, a timed lockout mechanism is
						implemented. Users are permitted three consecutive attempts to enter
						the correct code. If all three attempts fail, the system enters a
						high-security mode, automatically disabling all inputs for five
						minutes. The keypad remains inactive until the countdown is
						complete.

						<br />

						The system includes a dedicated reset function to return it to
						standby state after the door has been opened. A lid switch mounted
						to the door frame automatically triggers the reset upon closing. A
						visual indicator (light bulb) clearly signals that the door lock has
						been successfully disengaged.
					</p>
					<div className="doorlock__media">
						{renderFigure(
							circuitDiagramUrl,
							"Digital door lock breadboard prototype with keypad, seven-segment displays, and control circuitry",
							"Circuit diagram of the digital door lock system."
						)}
					</div>
				</div>
				<br />
				<br />

				<h1 className="text-head"> Components and Their Functions</h1>

				<div className="component-table">
					<table
						border="1"
						cellpadding="8"
						cellspacing="0"
						className="project-text"
						style={{ width: "100%", borderCollapse: "collapse" }}
					>
						<thead style={{ backgroundColor: "#5e93cf70" }}>
							<tr>
								<th>Component</th>
								<th>Function</th>
								<th>Use Case in Circuit</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>74HC4511</td>
								<td>BCD to 7-Segment Decoder/Driver</td>
								<td>Converts 4-bit BCD input into 7-segment display output.</td>
							</tr>
							<tr>
								<td>7-Segment Display</td>
								<td>Numeric Display</td>
								<td>Displays the set password digits visually.</td>
							</tr>
							<tr>
								<td>DIP Switch (SW1, SW2, SW3)</td>
								<td>Manual Binary Input</td>
								<td>Used to set the stored password digits.</td>
							</tr>
							<tr>
								<td>74HC85</td>
								<td>4-bit Magnitude Comparator</td>
								<td>Compares entered digit with stored password digit.</td>
							</tr>
							<tr>
								<td>Keypad</td>
								<td>User Input Device</td>
								<td>Allows user to enter password digits.</td>
							</tr>
							<tr>
								<td>7493</td>
								<td>4-bit Binary Ripple Counter</td>
								<td>Tracks input position and counts wrong attempts.</td>
							</tr>
							<tr>
								<td>CD4043</td>
								<td>Quad S-R Latch</td>
								<td>Stores entered password digits temporarily.</td>
							</tr>
							<tr>
								<td>74HC08</td>
								<td>AND Gate</td>
								<td>
									Combines correct comparison outputs to generate unlock signal.
								</td>
							</tr>
							<tr>
								<td>74LS11</td>
								<td>3-Input AND Gate</td>
								<td>Ensures all digits match before unlocking.</td>
							</tr>
							<tr>
								<td>555 Timer</td>
								<td>Monostable Timer</td>
								<td>Generates delay after wrong attempts.</td>
							</tr>
							<tr>
								<td>NPN Transistor (Q1, Q2)</td>
								<td>Switching Device</td>
								<td>Drives the lock/relay and timer control circuit.</td>
							</tr>
							<tr>
								<td>Diodes (1N4148)</td>
								<td>Signal Isolation / Protection</td>
								<td>Prevents reverse current and protects logic signals.</td>
							</tr>
							<tr>
								<td>OK Switch</td>
								<td>Manual Override</td>
								<td>Allows manual unlock of the door.</td>
							</tr>
							<tr>
								<td>Reset Switch</td>
								<td>Manual Reset Control</td>
								<td>Resets counters and unlock logic to initial state.</td>
							</tr>
						</tbody>
					</table>
				</div>
				<br />
				<br />

				<h1 className="text-head"> Prototyping and Testing</h1>

				<div className="testing-with-image">
					<div>
						<p className="project-text">
							Before final implementation, I constructed and tested the complete
							circuit using a breadboard to validate the logic design and system
							functionality. This prototyping stage allowed me to carefully
							verify each module, including the keypad input, counters, latches,
							comparators, and timer-based lockout mechanism. By assembling the
							components step by step on the breadboard, I was able to
							troubleshoot wiring issues, confirm signal flow, and ensure
							accurate synchronization between digital logic stages. The
							breadboard testing phase played a crucial role in refining the
							circuit, improving reliability, and confirming that the unlocking
							and security features operated exactly as intended before moving
							toward a finalized hardware layout.
						</p>
					</div>
					<div className="doorlock__media">
						{renderFigure(
							testingUrl,
							"Digital door lock breadboard prototype with keypad, seven-segment displays, and control circuitry",
							"Breadboard prototype."
						)}
					</div>
				</div>
				<br />
				<br />

				<h1 className="text-head"> Implementation</h1>

				<div className="project-container">
					<p className="project-text">
						After successfully validating the circuit on the breadboard, I
						proceeded to the implementation phase by designing the PCB layout
						using EasyEDA. The schematic was carefully translated into a
						structured PCB design, ensuring proper component placement,
						optimized routing paths, and minimal signal interference. Special
						attention was given to power distribution, grounding, and spacing
						between digital logic components to maintain circuit stability and
						reliability.
					</p>
					<br />

					<div className="doorlock__media_cir_and_pcb">
						{renderFigure(
							dispcir,
							"Display module circuit schematic",
							"Display circuit (schematic)"
						)}
						{renderFigure(
							disppcb,
							"Display module PCB layout",
							"Display PCB layout"
						)}
						{renderFigure(
							keycir,
							"Keypad circuit schematic",
							"Keypad circuit (schematic)"
						)}
						{renderFigure(
							keypcb,
							"Keypad PCB layout",
							"Keypad PCB layout"
						)}
					</div>
					<br />
					<br />
					<div className="final-pcb">
						<p className="project-text">
							Once the PCB layout was finalized, I manually printed the circuit
							design and carried out the fabrication process. This involved
							transferring the PCB pattern, etching the board, drilling
							component holes, and soldering each component with precision.
							Through this hands-on manufacturing process, I gained practical
							experience in PCB production, soldering techniques, and hardware
							assembly, successfully transforming the digital lock system from a
							prototype into a fully functional hardware implementation.
						</p>

						<div className="doorlock__media_cir_and_pcb">
							{renderFigure(
								finalpcb,
								"Final PCB layout",
								"Final PCB layout"
							)}
						</div>
					</div>
					<br />
					<br />
				</div>

				<h1 className="text-head"> Final Product</h1>

				<div className="final-product">
					<p className="project-text">
						Finally, all components, including the keypad, 7-segment display, reset
						mechanism, and timer are integrated into a compact, working pcb.
						The system responds accurately to both correct
						and incorrect inputs, provides real-time visual feedback, and
						enforces security protocols effectively. The video
						demonstration presents the system in action, including entering the
						security code, real-time display feedback, unlocking with the
						correct code, and triggering the reset or lockout after incorrect
						attempts with technical functionality and user experience.
					</p>
					<div className="final-product__image">
						{renderFigure(
							finalphoto,
							"Assembled PCB with keypad and display",
							null
						)}
					</div>
					<div className="final-product__video">
						<iframe
							src="https://1drv.ms/v/c/c94efc88b6ce9f63/IQQQwxJAgGfuRJX7XVhuCGoLAeID5yTIojM0UMdsmgWSya4"
							width="auto"
							height="320"
							frameBorder="0"
							scrolling="no"
							allowFullScreen
							title="Door lock demo video"
						></iframe>
					</div>
				</div>
				<br />
				<br />

				<h1 className="text-head"> Conclusion</h1>

				<div className="doorlock__content">
					<div className="doorlock__panel">
						<h2>Highlights</h2>
						<ul className="project-text">
							<li>3x4 keypad for passcode input with OK and Reset.</li>
							<li>
								Latch-driven lock control using combinational gates and
								flip-flops.
							</li>
							<li>
								LED indicators for locked/unlocked/error states (only for
								debug).
							</li>
							<li>
								Reset circuitry to clear attempts and restore default state.
							</li>
						</ul>
					</div>

					<div className="doorlock__panel">
						<h2>Tech Stack</h2>
						<ul className="project-text">
							<li>Logic ICs: flip-flops, AND/OR/NOT gates.</li>
							<li>3x4 matrix keypad for user input.</li>
							<li>Discrete LEDs for feedback.</li>
							<li>Driver stage to actuate the lock.</li>
						</ul>
					</div>

					<div className="doorlock__panel">
						<h2>Flow</h2>
						<ol className="project-text">
							<li>Idle: lock engaged, waiting for key sequence.</li>
							<li>
								Input: user enters passcode; invalid sequences trigger error
								state.
							</li>
							<li>
								Unlock: correct code sets latch, drives relay, and lights
								success indicator.
							</li>
							<li>Reset: clears state to accept the next entry.</li>
							<li>
								Security: 3 failed attempts trigger a timed lockout, disabling
								inputs for 5 minutes.
							</li>
						</ol>
					</div>
				</div>
			</section>
		</main>
	);
}

export default DoorLock;
